import Box from '@/common/components/Box'
import ResumeItem from '@/common/components/ResumeItem'
import useCompany from '@/common/hooks/queries/useCompany'
import { cn } from '@/common/utils/cn'
import { formatCurrency } from '@/common/utils/formatters'
import FinancialReviewEntrySimpleEntryApprove from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/FinancialReviewEntry/SimpleEntry/Approve'
import FinancialReviewEntrySimpleEntryReject from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/FinancialReviewEntry/SimpleEntry/Reject'
import { maintenanceForwardingsDirectiveEntryKlasses } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.constant'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { MaintenanceForwardingStatus } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import useGetDirectiveEntryObjectivesSummary from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useGetDirectiveEntryObjectivesSummary'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

const MaintenanceWorkflowForwardingFinancialReviewEntrySection = () => {
  const { workflow, forwarding, directiveEntryKlass } =
    useForwardingTabContext<MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry>()

  const { companyId } = useParams()

  const directiveEntryObjectivesSummary = useGetDirectiveEntryObjectivesSummary()

  const { data: company, dataUpdatedAt: companyUpdatedAt } = useCompany({ id: companyId || '' })

  const remainingCreditAfterApproval = useMemo(() => {
    if (!company) {
      return undefined
    }

    const companyRemainingCredit = (company?.remaining_credit && parseFloat(company?.remaining_credit)) || 0

    const maintenanceTotalValue =
      (forwarding?.directive_entry?.diagnostic?.total_value &&
        parseFloat(forwarding?.directive_entry.diagnostic.total_value)) ||
      0

    return companyRemainingCredit - maintenanceTotalValue
  }, [companyUpdatedAt])

  const isOnGoingFinancialReview = useMemo(() => {
    return (
      workflow?.current_forwarding?.id === forwarding?.id &&
      forwarding?.status === MaintenanceForwardingStatus.OnGoingFinancialReview
    )
  }, [workflow?.current_forwarding?.id, forwarding?.status])

  const isFinancialReviewApproved = useMemo(() => {
    return (
      !isOnGoingFinancialReview &&
      workflow?.current_forwarding?.id === forwarding?.id &&
      workflow?.current_forwarding?.financial_review_entry?.approved_at
    )
  }, [
    isOnGoingFinancialReview,
    workflow?.current_forwarding?.id,
    workflow?.current_forwarding?.financial_review_entry?.approved_at,
  ])

  return (
    <Box className={cn('p-4 flex flex-col gap-4', isOnGoingFinancialReview && 'bg-yellow-op-2')}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">Análise financeira</p>
        </div>

        {isOnGoingFinancialReview && (
          <div className="flex items-center gap-3">
            <FinancialReviewEntrySimpleEntryReject />

            <FinancialReviewEntrySimpleEntryApprove />
          </div>
        )}

        {isFinancialReviewApproved && (
          <div>
            <p className="text-valid">Aprovado</p>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <ResumeItem
          label={`Custo total de ${directiveEntryKlass && maintenanceForwardingsDirectiveEntryKlasses[directiveEntryKlass]?.label}`}
          value={
            (forwarding?.directive_entry?.diagnostic?.total_value &&
              formatCurrency(parseFloat(forwarding?.directive_entry.diagnostic.total_value))) ||
            '--'
          }
          className="text-xs"
          valueClassName="font-bold text-sm"
        />

        <ResumeItem
          label="Custo em Peças"
          value={
            (directiveEntryObjectivesSummary.piecesTotalValue !== undefined &&
              formatCurrency(directiveEntryObjectivesSummary.piecesTotalValue)) ||
            '--'
          }
          className="text-xs"
          valueClassName="font-bold text-sm"
        />

        <ResumeItem
          label="Custo em Serviços"
          value={
            (directiveEntryObjectivesSummary.activitiesTotalValue !== undefined &&
              formatCurrency(directiveEntryObjectivesSummary.activitiesTotalValue)) ||
            '--'
          }
          className="text-xs"
          valueClassName="font-bold text-sm"
        />
      </div>

      <div className="flex gap-3">
        <ResumeItem
          label="Crédito disponível"
          value={(company?.remaining_credit && formatCurrency(parseFloat(company?.remaining_credit))) || '--'}
          className="text-xs"
          valueClassName="font-bold text-sm"
        />

        <ResumeItem
          label="Crédito após aprovado"
          value={(remainingCreditAfterApproval !== undefined && formatCurrency(remainingCreditAfterApproval)) || '--'}
          className="text-xs"
          valueClassName="font-bold text-sm"
        />
      </div>
    </Box>
  )
}

export default MaintenanceWorkflowForwardingFinancialReviewEntrySection
